import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DiscountTimer } from "@/components/DiscountTimer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Sparkles, CreditCard, Info, Clock, Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Particles } from "@/components/Particles";
import PromoVideoPopup from "@/components/PromoVideoPopup";
import { CouponBanner } from "@/components/CouponBanner";
import { CustomerData } from "@/components/CustomerDataForm";
import { PixCheckout } from "@/components/PixCheckout";
import LoginPromptModal from "@/components/LoginPromptModal";
import { ALL_PACKAGES, TribopayPackage } from "@/constants/tribopayPackages";
import { getHotmartProductByPackageId } from "@/constants/hotmartProducts";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackMetaEvent } from "@/hooks/useMetaPixel";
import { usePurchaseCooldown } from "@/hooks/usePurchaseCooldown";

// External gateway checkout URLs (international credit/debit card)
const GATEWAY_CHECKOUT_URLS: Record<string, string> = {
  pack_basic: "https://www.llabs.art/checkout/100",
  pack_pro: "https://www.llabs.art/checkout/200",
  pack_supreme: "https://www.llabs.art/checkout/600",
};

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose }) => {
  // Temporary flags to toggle payment buttons while maintaining the code
  const SHOW_TRIBOPAY_BUTTON = true;
  const SHOW_GATEWAY_BUTTON = false;
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { cooldowns } = usePurchaseCooldown();
  const [showPromoVideo, setShowPromoVideo] = useState(false);
  const [showPixCheckout, setShowPixCheckout] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<TribopayPackage | null>(null);
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"pix" | "card">("pix");
  const [openTooltips, setOpenTooltips] = useState<Record<string, boolean>>({});
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
  const [loadingPackageId, setLoadingPackageId] = useState<string | null>(null);
  // Track if InitiateCheckout was already fired for this modal open
  const hasTrackedCheckout = useRef(false);
  const PROMO_VIDEO_SHOWN_KEY = "promo_video_popup_shown";
  useEffect(() => {
    if (isOpen) {
      // Track Meta Pixel InitiateCheckout event (only once per modal open)
      if (!hasTrackedCheckout.current) {
        trackMetaEvent("InitiateCheckout", {
          content_name: "Credits Package",
          content_category: "Credits",
        });
        hasTrackedCheckout.current = true;
      }

      // Check if promo video was already shown this session
      const hasSeenPromoVideo = sessionStorage.getItem(PROMO_VIDEO_SHOWN_KEY);
      if (!hasSeenPromoVideo) {
        const timer = setTimeout(() => {
          setShowPromoVideo(true);
          sessionStorage.setItem(PROMO_VIDEO_SHOWN_KEY, "true");
        }, 1000);
        return () => clearTimeout(timer);
      }
    } else {
      setShowPromoVideo(false);
      hasTrackedCheckout.current = false; // Reset when modal closes
    }
  }, [isOpen]);
  const handleSubscribeSupreme = async () => {
    setShowPromoVideo(false);
    const supremePackage = ALL_PACKAGES.find((pkg) => pkg.id === "pack_supreme");
    if (supremePackage) {
      // Usar o mesmo fluxo do handleSelectPackage para PIX
      await handleSelectPackage(supremePackage, "pix");
    }
  };
  const handleSelectPackage = async (pkg: TribopayPackage, method: "pix" | "card") => {
    // Verificar se usuário está autenticado
    if (!user) {
      toast({
        title: t("pricing.loginRequired"),
        description: t("pricing.loginRequiredDesc"),
        variant: "destructive",
      });
      setIsLoginPromptOpen(true);
      return;
    }
    setSelectedPackage(pkg);
    setPaymentMethod(method);

    // Se for cartão, iniciar checkout diretamente
    if (method === "card") {
      await handleCardCheckout(pkg);
    } else {
      // Se for PIX, buscar dados do perfil automaticamente
      try {
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("full_name, email")
          .eq("id", user.id)
          .single();
        if (profileError) {
          console.error("Error fetching profile:", profileError);
          toast({
            title: t("pricing.errorProcessing"),
            description: t("pricing.tryAgain"),
            variant: "destructive",
          });
          return;
        }

        // Preencher dados automaticamente
        const autoFilledData: CustomerData = {
          name: profile.full_name || "Cliente",
          email: profile.email || user.email || "",
          phone: "00000000000",
          document: "38398228822",
        };
        setCustomerData(autoFilledData);
        setShowPixCheckout(true);
      } catch (error) {
        console.error("Error auto-filling customer data:", error);
        toast({
          title: t("pricing.errorProcessing"),
          description: t("pricing.tryAgain"),
          variant: "destructive",
        });
      }
    }
  };
  const handleCardCheckout = async (pkg: TribopayPackage) => {
    setLoadingPackageId(pkg.id);

    // Abrir janela ANTES do await para não ser bloqueado pelo Safari
    const newWindow = window.open("about:blank", "_blank");

    // Injetar página de loading branded na janela aberta
    if (newWindow) {
      newWindow.document.write(
        `<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><title>Redirecting to checkout...</title><style>*{box-sizing:border-box}html,body{margin:0;padding:0;width:100%;height:100%}body{display:flex;align-items:center;justify-content:center;background:#09090b;color:#fafafa;font-family:system-ui,sans-serif}.container{display:flex;flex-direction:column;align-items:center;justify-content:center}.spinner{width:40px;height:40px;border:3px solid #27272a;border-top-color:#d4af37;border-radius:50%;animation:spin .8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}p{margin-top:16px;color:#a1a1aa;font-size:14px}</style></head><body><div class="container"><div class="spinner"></div><p>Redirecting to checkout...</p></div></body></html>`,
      );
      newWindow.document.close();
    }

    try {
      // Garantir sessão autenticada
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      if (!session) {
        if (newWindow) newWindow.close();
        toast({
          title: t("pricing.loginRequired"),
          description: t("pricing.loginRequiredDesc"),
          variant: "destructive",
        });
        setIsLoginPromptOpen(true);
        return;
      }

      // Buscar dados do perfil do usuário
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("full_name, email")
        .eq("id", user.id)
        .single();
      if (profileError) {
        if (newWindow) newWindow.close();
        console.error("Error fetching profile:", profileError);
        toast({
          title: t("pricing.errorProcessing"),
          description: t("pricing.tryAgain"),
          variant: "destructive",
        });
        return;
      }

      // Criar transação rastreável e obter URL do checkout
      const { data: response, error } = await supabase.functions.invoke("create-tribopay-tracked-checkout", {
        body: {
          packageId: pkg.id,
          prefillData: {
            name: profile?.full_name || "",
            email: profile?.email || "",
          },
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) {
        if (newWindow) newWindow.close();
        throw new Error(error.message || "Falha ao criar checkout");
      }
      if (!response?.success || !response?.checkoutUrl) {
        if (newWindow) newWindow.close();
        throw new Error(response?.error || "Falha ao criar checkout");
      }

      // Redirecionar a janela já aberta para o checkout
      if (newWindow) {
        newWindow.location.href = response.checkoutUrl;
      } else {
        // Fallback: redirecionar na mesma aba se popup foi bloqueado
        window.location.href = response.checkoutUrl;
      }
    } catch (error) {
      console.error("Error creating tracked checkout:", error);
      toast({
        title: t("pricing.errorProcessing"),
        description: error instanceof Error ? error.message : t("pricing.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setLoadingPackageId(null);
    }
  };
  const handlePixCheckoutClose = () => {
    setShowPixCheckout(false);
    setSelectedPackage(null);
    setCustomerData(null);
  };
  const handleHotmartCheckout = (pkg: TribopayPackage) => {
    // Verificar se usuário está autenticado
    if (!user) {
      toast({
        title: t("pricing.loginRequired"),
        description: t("pricing.loginRequiredDesc"),
        variant: "destructive",
      });
      setIsLoginPromptOpen(true);
      return;
    }

    // Verificar se existe produto Hotmart para este pacote
    const hotmartProduct = getHotmartProductByPackageId(pkg.id);
    if (!hotmartProduct) {
      toast({
        title: "Error",
        description: "Hotmart checkout not available for this package",
        variant: "destructive",
      });
      return;
    }

    // Construir URL com parâmetros sck (User ID) e email
    const userEmail = user?.email || "";
    const userId = user?.id || "";
    const checkoutUrlWithParams = `${hotmartProduct.checkoutUrl}?sck=${userId}&email=${encodeURIComponent(userEmail)}`;

    // Abrir checkout diretamente em nova aba
    window.open(checkoutUrlWithParams, "_blank", "noopener,noreferrer");
    toast({
      title: "Checkout opened",
      description: "Complete your purchase in the new tab.",
    });
  };
  const handleGatewayCheckout = async (pkg: TribopayPackage) => {
    if (!user) {
      toast({
        title: t("pricing.loginRequired"),
        description: t("pricing.loginRequiredDesc"),
        variant: "destructive",
      });
      setIsLoginPromptOpen(true);
      return;
    }
    // Map internal pack id → backend package slug
    const packageSlug = pkg.id === "pack_basic" ? "basic"
      : pkg.id === "pack_pro" ? "pro"
      : pkg.id === "pack_supreme" ? "supreme" : null;
    if (!packageSlug) {
      toast({
        title: "Error",
        description: "Gateway checkout not available for this package.",
        variant: "destructive",
      });
      return;
    }

    // Open popup synchronously (Safari-safe) while we fetch the URL
    // NOTE: do NOT use noopener/noreferrer here — we need the window handle
    // to set popup.location.href after the async fetch resolves.
    const popup = window.open("about:blank", "_blank");
    try {
      if (popup) {
        popup.document.write(
          '<html><head><title>Loading checkout…</title></head><body style="background:#0f172a;color:#fff;font-family:system-ui;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;">Loading secure checkout…</body></html>'
        );
      }
      const { data, error } = await supabase.functions.invoke("create-gateway-checkout", {
        body: { packageId: packageSlug },
      });
      if (error || !data?.checkoutUrl) {
        throw new Error(error?.message || "Failed to create checkout");
      }
      trackMetaEvent("InitiateCheckout", {
        content_name: pkg.name,
        content_category: "Credits",
        value: Number(pkg.priceDisplay) || undefined,
        currency: "USD",
      });
      if (popup) {
        popup.location.href = data.checkoutUrl;
      } else {
        window.location.href = data.checkoutUrl;
      }
      toast({ title: "Checkout opened", description: "Complete your purchase in the new tab." });
    } catch (e) {
      if (popup) popup.close();
      console.error("[handleGatewayCheckout] error:", e);
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Could not open checkout. Please try again.",
        variant: "destructive",
      });
    }
  };
  return (
    <>
      <PromoVideoPopup
        isOpen={showPromoVideo}
        onClose={() => {
          setShowPromoVideo(false);
          // Scroll to Supreme plan on mobile after popup closes
          if (isMobile) {
            setTimeout(() => {
              const supremeCard = document.getElementById("supreme-plan");
              if (supremeCard) {
                supremeCard.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }, 100);
          }
        }}
        onSubscribe={handleSubscribeSupreme}
      />
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent className="max-w-6xl w-full max-h-[90vh] p-6 bg-background/95 backdrop-blur-xl border-premium overflow-y-auto overflow-x-hidden [&>button]:z-50">
          <DialogTitle className="sr-only">{t("pricing.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("pricing.subtitle")}</DialogDescription>
          <div className="relative -m-6">
            {/* Particles Background */}
            <div className="">
              <Particles />
            </div>

            {/* Gradient Background - Cores Quentes */}
            <div className="" />
            <div className="" />

            {/* Content */}
            <div className="relative z-10 p-6 md:p-8 lg:p-12 pointer-events-auto">
              <div className="text-center mb-8 md:mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-premium border-premium mb-6">
                  <Sparkles
                    className="w-4 h-4"
                    style={{
                      color: "hsl(var(--gold))",
                    }}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{
                      color: "hsl(var(--gold))",
                    }}
                  >
                    {t("nav.loversPlus")}
                  </span>
                </div>

                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{t("pricing.subtitle")}</p>
              </div>

              <CouponBanner />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
                {ALL_PACKAGES.map((pkg) => (
                  <Card
                    key={pkg.id}
                    id={pkg.id === "pack_supreme" ? "supreme-plan" : undefined}
                    className={`relative glass p-6 md:p-8 transition-all duration-300 border-2 ${pkg.popular ? "border-premium hover-glow-gold" : pkg.id === "pack_pro" ? "border-gold/20 bg-gold/[0.02]" : "border-border"} overflow-visible`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 gradient-premium rounded-full shadow-elegant">
                        <span className="text-xs font-bold text-white">{t("pricing.mostIrresistible")}</span>
                      </div>
                    )}

                    <div className="text-center mb-6">
                      {pkg.name === "Supreme" ? (
                        <h3 className="text-3xl font-bold mb-2 text-gold">{pkg.name}</h3>
                      ) : (
                        <h3 className="text-2xl font-bold mb-2 text-foreground">{pkg.name}</h3>
                      )}
                      <div className="mb-4 flex items-baseline justify-center gap-2">
                        <span className="text-lg text-muted-foreground line-through">$ {pkg.originalPrice}</span>
                        {pkg.popular ? (
                          <span className="text-4xl md:text-5xl font-bold text-gold">$ {pkg.priceDisplay}</span>
                        ) : (
                          <span className="text-4xl md:text-5xl font-bold text-foreground">$ {pkg.priceDisplay}</span>
                        )}
                        <TooltipProvider>
                          <Tooltip
                            delayDuration={0}
                            open={isMobile ? openTooltips[pkg.id] : undefined}
                            onOpenChange={(open) => {
                              if (isMobile) {
                                setOpenTooltips((prev) => ({
                                  ...prev,
                                  [pkg.id]: open,
                                }));
                              }
                            }}
                          >
                            <TooltipTrigger asChild>
                              <button
                                className="p-0 m-0 bg-transparent border-none cursor-pointer hover:text-gold transition-colors"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isMobile) {
                                    setOpenTooltips((prev) => ({
                                      ...prev,
                                      [pkg.id]: !prev[pkg.id],
                                    }));
                                  }
                                }}
                              >
                                <Info className="w-4 h-4 text-muted-foreground" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-[280px] text-center pointer-events-auto z-[100]"
                              onPointerDownOutside={() => {
                                if (isMobile) {
                                  setOpenTooltips((prev) => ({
                                    ...prev,
                                    [pkg.id]: false,
                                  }));
                                }
                              }}
                            >
                              <p className="text-sm">
                                The final price may vary due to US dollar exchange rate fluctuations. The exact amount
                                will be shown at checkout.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${pkg.popular ? "glass-premium border-premium" : "bg-muted/50"}`}
                      >
                        <span className={`text-sm font-medium ${pkg.popular ? "text-gold" : "text-muted-foreground"}`}>
                          {pkg.credits} {t("pricing.credits")}
                        </span>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {pkg.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <div
                            className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${pkg.popular ? "bg-gold/20" : "bg-muted/50"}`}
                          >
                            <Check className={`w-3 h-3 ${pkg.popular ? "text-gold" : "text-muted-foreground"}`} />
                          </div>
                          <span className="text-sm text-foreground/80">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="space-y-3">
                      {SHOW_GATEWAY_BUTTON && (
                        <div className="space-y-1">
                          {/* Buy Credits — external gateway (international card) */}
                          <Button
                            onClick={() => handleGatewayCheckout(pkg)}
                            variant={pkg.popular ? "gradient" : "outline"}
                            className={`w-full ${pkg.popular ? "shadow-elegant" : "border-gold/40 hover:bg-gold/10 text-gold"}`}
                            size="lg"
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Buy Credits
                          </Button>
                          <p className="text-[10px] text-center text-muted-foreground">
                            International credit / debit card
                          </p>
                        </div>
                      )}

                      {SHOW_TRIBOPAY_BUTTON && (
                        <>
                          {SHOW_GATEWAY_BUTTON && (
                            <>
                              {/* Divider */}
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-px bg-border/50" />
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">or</span>
                                <div className="flex-1 h-px bg-border/50" />
                              </div>
                            </>
                          )}

                          {(() => {
                            const packageCooldown = cooldowns[pkg.id];
                            const isOnCooldown = packageCooldown?.isOnCooldown ?? false;
                            const hoursRemaining = packageCooldown?.hoursRemaining ?? 0;
                            const isLoading = loadingPackageId === pkg.id;

                            return (
                              <Button
                                onClick={() => handleSelectPackage(pkg, "card")}
                                variant={pkg.popular ? "gradient" : "outline"}
                                className={`w-full ${pkg.popular ? "shadow-elegant" : "border-gold/30 hover:bg-gold/10"} ${isOnCooldown || isLoading ? "opacity-60" : ""}`}
                                size="lg"
                                disabled={isOnCooldown || isLoading}
                              >
                                {isOnCooldown ? (
                                  <>
                                    <Clock className="w-4 h-4 mr-2" />
                                    Available in {hoursRemaining}h
                                  </>
                                ) : isLoading ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    {pkg.id === "pack_basic"
                                      ? "Buy Credits"
                                      : pkg.id === "pack_pro"
                                        ? "Buy Credits"
                                        : "Buy Credits"}
                                  </>
                                )}
                              </Button>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              <div className="mt-8 text-center space-y-1">
                <p className="text-sm text-muted-foreground">{t("pricing.footer")}</p>
                <p className="text-sm text-muted-foreground"></p>
                <p className="text-xs text-muted-foreground">
                  Contact{" "}
                  <a
                    href="mailto:support@loverslabs.art"
                    className="text-gold hover:text-gold-light underline underline-offset-2 transition-colors"
                  >
                    support@loverslabs.art
                  </a>
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {selectedPackage && customerData && (
        <PixCheckout
          isOpen={showPixCheckout}
          onClose={handlePixCheckoutClose}
          package={selectedPackage}
          customerData={customerData}
        />
      )}

      <LoginPromptModal
        isOpen={isLoginPromptOpen}
        onClose={() => setIsLoginPromptOpen(false)}
        redirectPath="/dashboard"
        redirectState={{
          openPricing: true,
        }}
      />
    </>
  );
};
